import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

const imageVariants = cva("object-cover", {
  variants: {
    shape: {
      default: "",
      rounded: "rounded-md",
      circle: "rounded-full",
    },
    shadow: {
      true: "shadow-md",
      false: "",
    },
  },
  defaultVariants: {
    shape: "default",
    shadow: false,
  },
});

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement>, VariantProps<typeof imageVariants> {
  src: string;
  alt: string;
  // TOOD: separate fallback and loading
  fallback?: React.ReactNode | "default";
  asChild?: boolean;
}

const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    { className, src, alt, fallback, shape, shadow, asChild = false, onLoad, onError, width, height, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "img";
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setLoading(false);
      onLoad?.(e);
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setLoading(false);
      setError(true);
      onError?.(e);
    };

    const containerStyle: React.CSSProperties = {};
    if (width) containerStyle.width = typeof width === "number" ? `${width}px` : width;
    if (height) containerStyle.height = typeof height === "number" ? `${height}px` : height;

    // TODO: Enhance error handling
    if (error) {
      return (
        <div style={containerStyle} className={cn("flex items-center justify-center", className)}>
          <i className="i-bp-failed size-6" />
        </div>
      );
    }

    return (
      <div style={containerStyle} className={cn("relative inline-block", className)}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* TODO: change default icon to loading */}
            {fallback === undefined ? <></> : fallback === "default" ? <i className="i-bp-failed size-6" /> : fallback}
          </div>
        )}
        <Comp
          className={cn("w-full h-full", imageVariants({ shape, shadow }), loading && "invisible")}
          src={src}
          alt={alt}
          ref={ref}
          onLoad={handleLoad}
          onError={handleError}
          width={width}
          height={height}
          {...props}
        />
      </div>
    );
  },
);
Image.displayName = "Image";

export { Image, imageVariants };
